import { useMemo } from 'react';

import { useLocalizedDate } from '@/hooks';

import type { OperationHistoryEvent } from '../OperationHistory.types';
import type { EventGroup } from './useOperationHistoryGrouping';

export interface DateBucket<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  label: string;
  groups: EventGroup<TEvent>[];
}

/**
 * Groups EventGroup[] into date buckets with localized labels for sticky dividers.
 * Input must already be sorted newest-first (output of useOperationHistoryGrouping).
 */
export function useOperationHistoryDateBuckets<
  TEvent extends OperationHistoryEvent,
>(groups: EventGroup<TEvent>[]): DateBucket<TEvent>[] {
  const { formatDateBucket } = useLocalizedDate();

  return useMemo(() => {
    if (groups.length === 0) {
      return [];
    }

    const buckets: DateBucket<TEvent>[] = [];
    let currentLabel: string | null = null;
    let currentBucket: DateBucket<TEvent> | null = null;

    for (const group of groups) {
      const label = formatDateBucket(group.timestamp);

      if (label !== currentLabel) {
        currentLabel = label;
        currentBucket = { label, groups: [group] };
        buckets.push(currentBucket);
      } else if (currentBucket) {
        currentBucket.groups.push(group);
      }
    }

    return buckets;
  }, [groups, formatDateBucket]);
}
